import { BaseScene } from './BaseScene';
import { t, setLang, getLang, SUPPORTED_LANGUAGES, LanguageInfo } from '../i18n/lang';
import { AudioManager } from '../systems/AudioManager';

export class SettingsScene extends BaseScene {
  protected override audioManager: AudioManager; // Changed to protected override

  constructor() {
    super({ key: 'Settings' });
  }

  override create(): void {
    super.create();
    this.addBackground();
    
    // Initialize audio manager
    this.audioManager = AudioManager.getInstance(this);
    
    this.createSettingsUI();
  }

  private createSettingsUI(): void {
    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;

    // Title
    this.add.text(centerX, 80, t('settings'), {
      fontSize: '36px',
      color: '#FFD700',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // Back button
    const backBtn = this.add.text(50, 50, t('back'), {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: '#666666',
      padding: { x: 15, y: 8 },
      fontFamily: 'Arial',
    })
    .setOrigin(0, 0.5)
    .setInteractive({ useHandCursor: true });

    backBtn.on('pointerdown', () => {
      this.audioManager.playButtonClick();
      this.scene.start('MainMenu');
    });

    backBtn.on('pointerover', () => {
      this.audioManager.playButtonHover();
      backBtn.setStyle({ backgroundColor: '#888888' });
    });

    backBtn.on('pointerout', () => {
      backBtn.setStyle({ backgroundColor: '#666666' });
    });

    // Settings sections
    let yOffset = 150;

    // Language Settings
    this.createLanguageSettings(centerX, yOffset);
    yOffset += 120;

    // Audio Settings
    this.createAudioControls(centerX, yOffset);
    yOffset += 200;

    // Visual Settings
    this.createVisualControls(centerX, yOffset);
  }

  private createLanguageSettings(centerX: number, startY: number): void {
    // Language settings title
    this.add.text(centerX, startY, t('language_settings'), {
      fontSize: '20px',
      color: '#4CAF50',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // Language selection
    this.add.text(centerX, startY + 30, t('select_language'), {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // Language buttons
    const buttonWidth = 140;
    const buttonSpacing = 160;
    const currentLang = getLang();
    
    SUPPORTED_LANGUAGES.forEach((langInfo: LanguageInfo, index: number) => {
      const isActive = currentLang === langInfo.code;
      const x = centerX - (SUPPORTED_LANGUAGES.length - 1) * buttonSpacing / 2 + index * buttonSpacing;
      
      const langBtn = this.add.text(x, startY + 60, `${langInfo.flag} ${langInfo.nativeName}`, {
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: isActive ? '#4CAF50' : '#555555',
        padding: { x: 10, y: 8 },
        fontFamily: 'Arial',
        align: 'center',
        wordWrap: { width: buttonWidth - 10 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

      if (!isActive) {
        langBtn.on('pointerdown', () => {
          this.audioManager.playButtonClick();
          setLang(langInfo.code);
          
          // Show confirmation dialog
          this.showLanguageChangeDialog(langInfo);
        });

        langBtn.on('pointerover', () => {
          this.audioManager.playButtonHover();
          langBtn.setStyle({ backgroundColor: '#777777' });
        });

        langBtn.on('pointerout', () => {
          langBtn.setStyle({ backgroundColor: '#555555' });
        });
      }
      
      // Store reference for updating
      langBtn.setName(`lang_${langInfo.code}`);
    });
  }

  private showLanguageChangeDialog(langInfo: LanguageInfo): void {
    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    
    // Background overlay
    const overlay = this.add.rectangle(0, 0, this.cameras.main.width, this.cameras.main.height, 0x000000, 0.7)
      .setOrigin(0)
      .setInteractive();
    
    // Dialog panel
    const panel = this.add.rectangle(centerX, centerY, 400, 200, 0x1a1a1a, 0.95)
      .setStrokeStyle(3, 0x4CAF50);
    
    // Title
    this.add.text(centerX, centerY - 60, t('language_settings'), {
      fontSize: '20px',
      color: '#4CAF50',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Message
    this.add.text(centerX, centerY - 20, `${t('select_language')}: ${langInfo.nativeName}`, {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Arial',
      align: 'center',
      wordWrap: { width: 350 },
    }).setOrigin(0.5);
    
    this.add.text(centerX, centerY + 10, t('restart') + '?', {
      fontSize: '14px',
      color: '#cccccc',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Buttons
    const confirmBtn = this.add.text(centerX - 50, centerY + 50, t('yes'), {
      fontSize: '16px',
      color: '#ffffff',
      backgroundColor: '#4CAF50',
      padding: { x: 15, y: 8 },
      fontFamily: 'Arial',
    })
    .setOrigin(0.5)
    .setInteractive({ useHandCursor: true });
    
    const cancelBtn = this.add.text(centerX + 50, centerY + 50, t('no'), {
      fontSize: '16px',
      color: '#ffffff',
      backgroundColor: '#f44336',
      padding: { x: 15, y: 8 },
      fontFamily: 'Arial',
    })
    .setOrigin(0.5)
    .setInteractive({ useHandCursor: true });
    
    confirmBtn.on('pointerdown', () => {
      this.audioManager.playButtonClick();
      // Restart the scene to apply new language
      this.scene.restart();
    });
    
    cancelBtn.on('pointerdown', () => {
      this.audioManager.playButtonClick();
      // Revert language change
      setLang(getLang());
      
      // Close dialog
      overlay.destroy();
      panel.destroy();
      this.children.list.filter(child => 
        child instanceof Phaser.GameObjects.Text && 
        (child.text.includes(langInfo.nativeName) || child.text.includes(t('language_settings')))
      ).forEach(child => child.destroy());
    });
    
    // Button hover effects
    confirmBtn.on('pointerover', () => {
      this.audioManager.playButtonHover();
      confirmBtn.setStyle({ backgroundColor: '#388E3C' });
    });
    
    confirmBtn.on('pointerout', () => {
      confirmBtn.setStyle({ backgroundColor: '#4CAF50' });
    });
    
    cancelBtn.on('pointerover', () => {
      this.audioManager.playButtonHover();
      cancelBtn.setStyle({ backgroundColor: '#d32f2f' });
    });
    
    cancelBtn.on('pointerout', () => {
      cancelBtn.setStyle({ backgroundColor: '#f44336' });
    });
  }

  private createAudioControls(centerX: number, startY: number): void {
    // Audio settings title
    this.add.text(centerX, startY, t('audio_settings'), {
      fontSize: '20px',
      color: '#2196F3',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    const audioSettings = this.audioManager.getSettings();
    let yOffset = 30;

    // Master Volume
    this.createVolumeSlider(
      t('master_volume'),
      centerX,
      startY + yOffset,
      audioSettings.masterVolume,
      (value: number) => this.audioManager.setMasterVolume(value)
    );
    yOffset += 60;

    // Music Volume
    this.createVolumeSlider(
      t('music_volume'),
      centerX,
      startY + yOffset,
      audioSettings.musicVolume,
      (value: number) => this.audioManager.setMusicVolume(value)
    );
    yOffset += 60;

    // SFX Volume
    this.createVolumeSlider(
      t('sfx_volume'),
      centerX,
      startY + yOffset,
      audioSettings.sfxVolume,
      (value: number) => this.audioManager.setSFXVolume(value)
    );
    yOffset += 60;

    // Mute Toggle
    const muteBtn = this.add.text(centerX, startY + yOffset, audioSettings.muted ? t('unmute') : t('mute'), {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: audioSettings.muted ? '#4CAF50' : '#f44336',
      padding: { x: 15, y: 8 },
      fontFamily: 'Arial',
    })
    .setOrigin(0.5)
    .setInteractive({ useHandCursor: true });

    muteBtn.on('pointerdown', () => {
      const currentMuted = this.audioManager.isMuted();
      this.audioManager.setMuted(!currentMuted);
      
      muteBtn.setText(currentMuted ? t('mute') : t('unmute'));
      muteBtn.setStyle({ backgroundColor: currentMuted ? '#f44336' : '#4CAF50' });
      
      if (!currentMuted) {
        this.audioManager.playButtonClick();
      }
    });

    muteBtn.on('pointerover', () => {
      if (!this.audioManager.isMuted()) {
        this.audioManager.playButtonHover();
      }
    });
  }

  private createVolumeSlider(label: string, x: number, y: number, initialValue: number, onChange: (value: number) => void): void {
    // Label
    this.add.text(x - 150, y, label, {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0, 0.5);

    // Slider track
    const track = this.add.rectangle(x + 50, y, 200, 8, 0x555555).setOrigin(0.5);
    
    // Slider fill
    const fill = this.add.rectangle(x + 50 - 100 + (initialValue * 200), y, initialValue * 200, 8, 0x4CAF50).setOrigin(0, 0.5);
    
    // Slider handle
    const handle = this.add.circle(x + 50 - 100 + (initialValue * 200), y, 12, 0xffffff)
      .setInteractive({ useHandCursor: true, draggable: true });

    // Value text
    const valueText = this.add.text(x + 170, y, `${Math.round(initialValue * 100)}%`, {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0, 0.5);

    // Handle drag
    handle.on('drag', (pointer: Phaser.Input.Pointer) => {
      const minX = x + 50 - 100;
      const maxX = x + 50 + 100;
      const clampedX = Phaser.Math.Clamp(pointer.x, minX, maxX);
      
      handle.setX(clampedX);
      
      const value = (clampedX - minX) / 200;
      fill.setDisplaySize(value * 200, 8);
      fill.setX(minX + (value * 200) / 2);
      
      valueText.setText(`${Math.round(value * 100)}%`);
      onChange(value);
    });

    // Click on track to jump
    track.setInteractive();
    track.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const minX = x + 50 - 100;
      const maxX = x + 50 + 100;
      const clampedX = Phaser.Math.Clamp(pointer.x, minX, maxX);
      
      handle.setX(clampedX);
      
      const value = (clampedX - minX) / 200;
      fill.setDisplaySize(value * 200, 8);
      fill.setX(minX + (value * 200) / 2);
      
      valueText.setText(`${Math.round(value * 100)}%`);
      onChange(value);
      
      this.audioManager.playButtonClick();
    });
  }

  private createVisualControls(centerX: number, startY: number): void {
    // Visual settings title
    this.add.text(centerX, startY, t('visual_settings'), {
      fontSize: '20px',
      color: '#FF9800',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    let yOffset = 30;

    // Show FPS toggle
    this.createToggle(t('show_fps'), centerX, startY + yOffset, 'showFPS');
    yOffset += 40;

    // Particle Effects toggle
    this.createToggle(t('particle_effects'), centerX, startY + yOffset, 'particleEffects');
    yOffset += 40;

    // Screen Shake toggle
    this.createToggle(t('screen_shake'), centerX, startY + yOffset, 'screenShake');
    yOffset += 60;

    // Quality presets
    this.add.text(centerX, startY + yOffset, t('quality_presets'), {
      fontSize: '16px',
      color: '#FF9800',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    yOffset += 30;

    const presets = [
      { key: 'low', label: t('low'), color: '#ff6b6b' },
      { key: 'medium', label: t('medium'), color: '#ffa726' },
      { key: 'high', label: t('high'), color: '#66bb6a' },
    ];

    presets.forEach((preset, index) => {
      const x = centerX - 120 + index * 120;
      const btn = this.add.text(x, startY + yOffset, preset.label, {
        fontSize: '16px',
        color: '#ffffff',
        backgroundColor: preset.color,
        padding: { x: 15, y: 8 },
        fontFamily: 'Arial',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

      btn.on('pointerdown', () => {
        this.audioManager.playButtonClick();
        this.applyQualityPreset(preset.key);
        this.showNotification(`${t('quality_presets')}: ${preset.label}`, 2000);
      });

      btn.on('pointerover', () => {
        this.audioManager.playButtonHover();
        const darkerColor = this.darkenColor(preset.color);
        btn.setStyle({ backgroundColor: darkerColor });
      });

      btn.on('pointerout', () => {
        btn.setStyle({ backgroundColor: preset.color });
      });
    });
  }

  private createToggle(label: string, x: number, y: number, settingKey: string): void {
    const isEnabled = this.getPerformanceSetting(settingKey);
    
    // Label
    this.add.text(x - 100, y, label, {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0, 0.5);

    // Toggle button
    const toggle = this.add.text(x + 100, y, isEnabled ? 'ON' : 'OFF', {
      fontSize: '16px',
      color: '#ffffff',
      backgroundColor: isEnabled ? '#4CAF50' : '#f44336',
      padding: { x: 20, y: 8 },
      fontFamily: 'Arial',
    })
    .setOrigin(0.5)
    .setInteractive({ useHandCursor: true });

    toggle.on('pointerdown', () => {
      this.audioManager.playButtonClick();
      const newValue = !this.getPerformanceSetting(settingKey);
      this.setPerformanceSetting(settingKey, newValue);
      
      toggle.setText(newValue ? 'ON' : 'OFF');
      toggle.setStyle({ backgroundColor: newValue ? '#4CAF50' : '#f44336' });
    });

    toggle.on('pointerover', () => {
      this.audioManager.playButtonHover();
      const currentBg = isEnabled ? '#4CAF50' : '#f44336';
      const darkerBg = this.darkenColor(currentBg);
      toggle.setStyle({ backgroundColor: darkerBg });
    });

    toggle.on('pointerout', () => {
      const currentEnabled = this.getPerformanceSetting(settingKey);
      toggle.setStyle({ backgroundColor: currentEnabled ? '#4CAF50' : '#f44336' });
    });
  }

  private getPerformanceSetting(key: string): boolean {
    const settings = JSON.parse(localStorage.getItem('performanceSettings') || '{}');
    return settings[key] !== false; // Default to true
  }

  private setPerformanceSetting(key: string, value: boolean): void {
    const settings = JSON.parse(localStorage.getItem('performanceSettings') || '{}');
    settings[key] = value;
    localStorage.setItem('performanceSettings', JSON.stringify(settings));
  }

  private applyQualityPreset(preset: string): void {
    const presets = {
      low: {
        showFPS: false,
        particleEffects: false,
        screenShake: false,
      },
      medium: {
        showFPS: false,
        particleEffects: true,
        screenShake: true,
      },
      high: {
        showFPS: true,
        particleEffects: true,
        screenShake: true,
      },
    };

    const settings = presets[preset as keyof typeof presets];
    Object.entries(settings).forEach(([key, value]) => {
      this.setPerformanceSetting(key, value);
    });

    // Restart scene to apply changes
    this.time.delayedCall(1000, () => {
      this.scene.restart();
    });
  }

  private darkenColor(colorString: string): string {
    // Simple color darkening (remove # if present, convert to darker shade)
    const color = colorString.replace('#', '');
    const rgb = parseInt(color, 16);
    const r = Math.max(0, ((rgb >> 16) & 255) - 30);
    const g = Math.max(0, ((rgb >> 8) & 255) - 30);
    const b = Math.max(0, (rgb & 255) - 30);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  }
} 